interface StorageService {
    /**
     * Get a value.
     * @param key The key to lookup the value with.
     */
    get(key: string): string | undefined;
    /**
     * Set a value.
     * @param key The key to set.
     * @param val The new value.
     */
    set(key: string, val: string): void;
    /**
     * Remove a value.
     * @param key The key to remove, does nothing if the key doesn't exist.
     */
    remove(key: string): void;
    /**
     * Get a map from persistence.
     * @param key The key to search by.
     */
    getMap<K = any, V = any>(key: string): Map<K, V> | undefined;
    /**
     * St a map into persistence.
     * @param key The key to set the map at.
     * @param val The map to set.
     */
    setMap<K = any, V = any>(key: string, val: Map<K, V>): void;
}

/**
 * Storage built upon browser localStorage capability.
 */
class LocalStorageService implements StorageService {
    get(key: string): string | undefined {
        return localStorage.getItem(key) || undefined;
    }
    set(key: string, val: string): void {
        localStorage.setItem(key, val);
    }
    remove(key: string): void {
        localStorage.removeItem(key);
    }
    getMap(key: string): Map<any, any> | undefined {
        const serialisedMap = this.get(key);
        if (!serialisedMap) {
            return;
        }
        return new Map(JSON.parse(serialisedMap));
    }
    setMap(key: string, val: Map<any, any>): void {
        const mapSerialised = JSON.stringify([...val.entries()]);
        this.set(key, mapSerialised);
    }
}

/**
 * An in-memory fallback storage when we encounter environments that don't support localStorage.
 */
class InMemoryStorageService implements StorageService {
    private storage: Map<string, string> = new Map();
    get(key: string): string | undefined {
        return this.storage.get(key) || undefined;
    }
    set(key: string, val: string): void {
        this.storage.set(key, val);
    }
    remove(key: string): void {
        this.storage.delete(key);
    }
    getMap(key: string): Map<any, any> | undefined {
        const serialisedMap = this.get(key);
        if (!serialisedMap) {
            return;
        }
        return new Map(JSON.parse(serialisedMap));
    }
    setMap(key: string, val: Map<any, any>): void {
        const mapSerialised = JSON.stringify([...val.entries()]);
        this.set(key, mapSerialised);
    }
}

const buildPersistentStorage = (): StorageService => {
    try {
        if (localStorage) {
            return new LocalStorageService();
        }
        return new InMemoryStorageService();
    } catch {
        console.warn("Local storage was unavilable due to browser security settings. Using in-memory storage instead.");
        return new InMemoryStorageService();
    }
};

export const persistenceService = buildPersistentStorage();
